import { Wrapper } from "@googlemaps/react-wrapper"
import { GoogleMap, GoogleMapMarker, renderMap } from "app/brands/components/Map"
import { card, h2 } from "app/common/components/Containers"
import { decodeWith } from "app/common/core/decode"
import { justThrow } from "app/common/fp-ts"
import { joinWithCommaSpace, joinWithSpace } from "app/common/fp-ts/array"
import { logException } from "app/common/logging"
import { optionalRender } from "app/common/ui/optionalRender"
import { LocationD } from "app/data/location/location.types"
import { BrandVM } from "app/models"
import { apply, array, either, option } from "fp-ts"
import { pipe } from "fp-ts/function"
import { string } from "io-ts"
import useTranslation from "next-translate/useTranslation"
import React from "react"
import { FC } from "react"
import { tw } from "twind"

interface BrandLocationsProps {
  brand: BrandVM
}

export const BrandLocations: FC<BrandLocationsProps> = ({ brand }) => {
  const { t } = useTranslation("brands")

  const apiKey = pipe(
    process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
    decodeWith(string),
    either.mapLeft(logException),
    either.getOrElseW(justThrow)
  )

  const [clicks, setClicks] = React.useState<google.maps.LatLng[]>([])

  const [zoom, setZoom] = React.useState(15)

  const onClick = (e: google.maps.MapMouseEvent) => {
    // avoid directly mutating state
    pipe(
      e.latLng,
      option.fromNullable,
      option.map((latLng) => setClicks([...clicks, latLng]))
    )
  }

  const onIdle = (m: google.maps.Map) => {
    pipe(m.getZoom(), option.fromNullable, option.map(setZoom))
  }

  return (
    <div className={tw(card)}>
      <h2 className={tw(h2)}>{t("location")}</h2>

      <ul className={tw`flex(& col) gap-10`}>
        {pipe(
          brand.locations,
          array.map((location: LocationD) => (
            <li className={tw(`flex(& col) gap-6`)} key={location.id}>
              <div>
                {pipe(
                  <ul>
                    <li>
                      <>
                        {pipe(
                          [location.streetNumber, location.street, location.unitNumber],
                          array.compact,
                          joinWithSpace
                        )}

                        {pipe(
                          location.postalCode,
                          option.map((pc) => `, ${pc}`),
                          option.toNullable
                        )}
                      </>
                    </li>
                    <li>
                      {pipe([location.city.name, location.city.province.name], joinWithCommaSpace)}
                    </li>
                  </ul>
                )}
              </div>
              {pipe(
                {
                  latitude: location.latitude,
                  longitude: location.longitude,
                  streetNumber: location.streetNumber,
                },
                apply.sequenceS(option.Applicative),
                optionalRender(({ latitude, longitude }) => {
                  const point = { lat: latitude, lng: longitude }

                  return (
                    <Wrapper apiKey={apiKey} libraries={["places"]} render={renderMap}>
                      <div className={tw(`h-80`)} key={JSON.stringify(point, null, 2)}>
                        <GoogleMap
                          center={point}
                          onClick={onClick}
                          onIdle={onIdle}
                          style={{ flexGrow: "1", height: "100%" }}
                          zoom={zoom}
                        >
                          <GoogleMapMarker position={point} />
                        </GoogleMap>
                      </div>
                    </Wrapper>
                  )
                })
              )}
            </li>
          ))
        )}
      </ul>
    </div>
  )
}
